/**
 * Contacts Integration for Call History
 * 
 * This module provides the integration between Webex Contacts and Call History enrichment.
 * It initializes the contacts module and passes it to the phone enrichment hook.
 * 
 * USAGE:
 * 
 * 1. Initialize contacts on app startup (see initializeContactsForEnrichment)
 * 2. Pass contacts to usePhoneEnrichment via options
 * 3. Enrichment will automatically check Contacts → DSS → Miss
 */

import { createContactsIntegrationSafe, type ContactsIntegration } from './contacts';
import { whenDeviceRegistered, whenWebexReady } from '../../packages/widgets/src/services/WebexInstanceService';

// Global contacts instance (or use React Context/Redux)
let globalContactsIntegration: ContactsIntegration | null = null;
let globalContactsInitialization: Promise<ContactsIntegration | null> | null = null;

export const CONTACTS_ENRICHMENT_READY_EVENT = 'contacts-enrichment-ready';
export const CONTACTS_REFRESHED_EVENT = 'contacts-refreshed';
export { CONTACTS_DATA_READY_EVENT, dispatchContactsDataReady } from './contacts/types';

/** Minimum interval between background contact syncs triggered by UI (e.g. Recent Calls tab). */
const CONTACTS_SYNC_THROTTLE_MS = 30_000;
let lastContactsSyncAt = 0;

function dispatchContactsRefreshed(userId?: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(CONTACTS_REFRESHED_EVENT, { detail: userId ? { userId } : undefined })
  );
}

/**
 * Initialize contacts for call history enrichment
 * Call this ONCE during app initialization, after Webex SDK is ready
 * 
 * @param webex - Webex SDK instance
 * @param userId - User ID for cache scoping
 * @returns Promise<void>
 * 
 * @example
 * ```typescript
 * // In App.tsx or main initialization
 * import { initializeContactsForEnrichment } from './services/phoneEnrichment/contactsIntegration';
 * 
 * async function initApp(webex, userId) {
 *   // ... other initialization ...
 *   
 *   await initializeContactsForEnrichment(webex, userId);
 *   
 *   console.log('App ready with contacts support!');
 * }
 * ```
 */
export async function initializeContactsForEnrichment(
  webex: any,
  userId: string
): Promise<void> {
  // If webex wasn't passed in (consumer called too early), try to wait for global instance
  if (!webex) {
    try {
      webex = await whenWebexReady(20000);
    } catch (err) {
      console.error('[Enrichment] ContactsIntegration: timed out waiting for global webex instance', err);
    }
  }

  if (webex?.internal?.device && !webex.internal.device.registered) {
    try {
      await whenDeviceRegistered(20000);
    } catch (err) {
      console.warn('[Enrichment] ContactsIntegration: timed out waiting for device registration', err);
    }
  }

  // Prevent duplicate initialization
  if (globalContactsIntegration !== null) {
    return;
  }

  if (globalContactsInitialization) {
    await globalContactsInitialization;
    return;
  }

  try {
    // Create contacts integration with safe error handling
    globalContactsInitialization = createContactsIntegrationSafe(webex, userId);
    globalContactsIntegration = await globalContactsInitialization;
    if (globalContactsIntegration && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(CONTACTS_ENRICHMENT_READY_EVENT, { detail: { userId } })
      );
    }
    
    // Don't check isReady here - contacts might still be initializing from API
    // Let the enrichment hook wait briefly for in-progress initialization
  } catch (error: any) {
    console.error('[Enrichment] ContactsIntegration: Failed to initialize contacts:', error.message);
    // Continue without contacts - enrichment will fall back to DSS
  }
  globalContactsInitialization = null;
}

/**
 * Get the contacts integration instance
 * Used internally by the enrichment system
 * 
 * @returns ContactsIntegration | null
 */
export function getContactsForEnrichment(): ContactsIntegration | null {
  return globalContactsIntegration;
}

/**
 * Check if contacts are available and ready for enrichment
 * 
 * @returns boolean
 */
export function areContactsReadyForEnrichment(): boolean {
  const hasIntegration = globalContactsIntegration !== null;
  const isResolverReady = hasIntegration ? globalContactsIntegration!.resolver.isReady() : false;
  const stats = hasIntegration ? globalContactsIntegration!.resolver.getTotalContacts() : 0;

  return hasIntegration && isResolverReady;
}

/**
 * Get contacts statistics
 * Useful for debugging and UI display
 * 
 * @returns Object with contacts stats
 */
export function getContactsStatsForEnrichment() {
  if (!globalContactsIntegration) {
    return {
      available: false,
      ready: false,
      totalContacts: 0,
      totalPhones: 0,
    };
  }

  const stats = globalContactsIntegration.manager.getStats();
  return {
    available: true,
    ready: globalContactsIntegration.resolver.isReady(),
    totalContacts: stats.totalContacts,
    totalPhones: stats.totalPhones,
    cacheAge: Date.now() - stats.fetchedAt,
    expiresIn: stats.expiresAt - Date.now(),
  };
}

/**
 * Sync contacts from the Webex Contacts API without clearing the local cache first.
 * Dispatches contacts-refreshed so call history re-enriches with new personal contacts.
 */
export async function syncContactsForEnrichment(): Promise<boolean> {
  if (!globalContactsIntegration) {
    console.warn('[ContactsIntegration] Cannot sync - contacts not initialized');
    return false;
  }

  try {
    console.log('[ContactsIntegration] Syncing contacts from API...');
    await globalContactsIntegration.resolver.syncFromApi();
    console.log('[ContactsIntegration] Contacts sync completed');
    dispatchContactsRefreshed();
    return true;
  } catch (error: any) {
    console.error('[ContactsIntegration] Failed to sync contacts:', error.message);
    return false;
  }
}

/**
 * Throttled contact sync for UI triggers (Recent Calls tab, etc.).
 */
export async function syncContactsForEnrichmentThrottled(): Promise<boolean> {
  const now = Date.now();
  if (now - lastContactsSyncAt < CONTACTS_SYNC_THROTTLE_MS) {
    return false;
  }
  lastContactsSyncAt = now;
  return syncContactsForEnrichment();
}

/**
 * Force refresh contacts from API (clears cache first).
 */
export async function refreshContactsForEnrichment(): Promise<boolean> {
  if (!globalContactsIntegration) {
    console.warn('[ContactsIntegration] Cannot refresh - contacts not initialized');
    return false;
  }

  try {
    console.log('[ContactsIntegration] Refreshing contacts...');
    await globalContactsIntegration.resolver.refresh();
    console.log('[ContactsIntegration] ✅ Contacts refreshed successfully');
    dispatchContactsRefreshed();
    return true;
  } catch (error: any) {
    console.error('[ContactsIntegration] Failed to refresh contacts:', error.message);
    return false;
  }
}

/**
 * Cleanup contacts on app unmount or logout
 * 
 * @example
 * ```typescript
 * // On logout or app cleanup
 * import { cleanupContactsForEnrichment } from './services/phoneEnrichment/contactsIntegration';
 * 
 * function logout() {
 *   cleanupContactsForEnrichment();
 *   // ... other cleanup ...
 * }
 * ```
 */
export function cleanupContactsForEnrichment(): void {
  console.log('[ContactsIntegration] Cleaning up contacts');
  globalContactsIntegration?.dispose();
  globalContactsIntegration = null;
}

/**
 * Create enrichment options with contacts support
 * Helper to prepare options for usePhoneEnrichment hook
 * 
 * @param baseOptions - Base enrichment options (userId, dss, etc.)
 * @returns Enhanced options with contacts integration
 * 
 * @example
 * ```typescript
 * import { usePhoneEnrichment } from './services/phoneEnrichment/usePhoneEnrichment';
 * import { createEnrichmentOptionsWithContacts } from './services/phoneEnrichment/contactsIntegration';
 * 
 * function CallHistoryComponent({ callHistory }) {
 *   const options = createEnrichmentOptionsWithContacts({
 *     userId: user.id,
 *     dss: webex.dss,
 *     enableEnrichment: true,
 *   });
 *   
 *   const { enrichedItems } = usePhoneEnrichment(callHistory, options);
 *   
 *   return <div>{enrichedItems.map(item => ...)}</div>;
 * }
 * ```
 */
export function createEnrichmentOptionsWithContacts(baseOptions: any) {
  const contacts = getContactsForEnrichment();

  return {
    ...baseOptions,
    contacts, // Add contacts to options (PhoneEnricher will use it)
  };
}
